import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import LoadingIndicator from 'components/LoadingIndicator';
import useAuthSession from 'hooks/useAuthSession';
import { useLazyFetchUserAuthenticationInfoQuery } from 'redux/api/user.api';
import { LOGIN_CALLBACK_FLOW_EXPIRATION } from 'utils/constants';
import localStorageUser from 'utils/localStorageUser';
import { validateEmail } from 'utils/validators';

const LoginCallbackPage = () => {
  const history = useHistory();
  const {
    isLoading,
    error,
    user,
    isAuthenticated,
    loginWithIDP,
    handleRedirectCallback,
    logout,
  } = useAuthSession();
  const processedRef = useRef(false);
  const [callbackProcessed, setCallbackProcessed] = useState(false);
  const [fetchUserInfo] = useLazyFetchUserAuthenticationInfoQuery();

  useEffect(
    () => {
      if (processedRef.current) return;
      processedRef.current = true;
      (async () => {
        try {
          await handleRedirectCallback();
        } catch (err) {
          console.error('Error processing auth callback:', err);
          sessionStorage.setItem(
            'loginCallbackError',
            JSON.stringify({
              code: err?.errorCode || err?.name || 'authentication_callback_failed',
              message: err?.message,
            }),
          );
          await logout();
          return;
        }
        setCallbackProcessed(true);
      })();
    },
    [handleRedirectCallback, history, logout],
  );

  useEffect(
    () => {
      if (!callbackProcessed) return;
      (async () => {
        if (isLoading || !history) return;

        const redirectOnLoginFailure = async err => {
          sessionStorage.setItem('loginCallbackError', JSON.stringify(err));
          await logout();
        };

        if (error) {
          await redirectOnLoginFailure(error);
          return;
        }

        if (!isAuthenticated || !user) return;

        const userInfo = await fetchUserInfo({
          email: user.email,
        }).unwrap();

        if (userInfo.exists && userInfo.is_email_verified) {
          await loginWithIDP();
          const userId = localStorageUser.getCurrentUserId();
          if (!userId) {
            await redirectOnLoginFailure({ code: 'account_exists_different_provider' });
          } else {
            history.replace('/');
          }
          return;
        }

        if (
          !userInfo.exists ||
          userInfo.invitations?.length ||
          userInfo.email_validation_required
        ) {
          let firstName = '';
          let lastName = '';
          if (user.given_name && user.family_name) {
            firstName = user.given_name;
            lastName = user.family_name;
          } else if (user.name && !validateEmail(user.name)) {
            [firstName, ...lastName] = user.name.split(' ');
            lastName = lastName.join(' ');
          }

          const validationCode = userInfo.validation_code || null;
          const flow = {
            userInfo: {
              ...userInfo,
              firstName,
              lastName,
              validationCode,
              email: user.email,
            },
            expiresAt: Date.now() + LOGIN_CALLBACK_FLOW_EXPIRATION,
          };
          sessionStorage.setItem('loginCallbackFlow', JSON.stringify(flow));
        }

        history.replace(`/login`);
      })();
    },
    [
      callbackProcessed,
      isLoading,
      error,
      user,
      isAuthenticated,
      history,
      loginWithIDP,
      fetchUserInfo,
    ],
  );

  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
      <LoadingIndicator withBackground={false} />
    </Box>
  );
};

export default LoginCallbackPage;
